import { useParams } from "react-router-dom";
import { Case, Determination, Donor, ProductCodeResponse, Referral } from "../models";
import { useCaseQuery, useTissueQuery } from "../queries";
import { useCallback, useMemo } from "react";

interface IUseCurrentCaseParams {
    includeDetails?: boolean;
    caseIdOverride?: number | string;
}

interface IUseCurrentCase {
    case: Case | undefined;
    caseId: number | undefined;
    referral: Referral | undefined;
    donor: Donor | undefined;
    determination: Determination | undefined;
    productCodes: ProductCodeResponse[] | undefined;
    getTissueProductCode: (tissueId: number) => string | undefined;
    isLoading: boolean;
}

export function useCurrentCase(params?: IUseCurrentCaseParams): IUseCurrentCase {
    const { caseId: caseIdFromUrl, tissueId } = useParams<{ caseId: string; tissueId: string }>();
    const includeDetails = params?.includeDetails ?? false;
    const caseId = params?.caseIdOverride ?? caseIdFromUrl;

    const {
        detailQuery: { data: tissue, isFetching: isTissueFetching },
    } = useTissueQuery({ id: tissueId });

    const useTissueId = !caseId && !!tissueId;

    const {
        detailQuery: { data: _case, isFetching: isCaseFetching },
    } = useCaseQuery({ id: useTissueId ? tissue?.caseId : caseId, includeDetails });

    const {
        productCodeQuery: { data: productCodes, isFetching: isProductCodesFetching },
    } = useCaseQuery({ id: useTissueId ? tissue?.caseId : caseId, queryType: "productCodes" });

    const getTissueProductCode = useCallback(
        (tissueId: number) => {
            const productCode = productCodes?.find((pc) => pc.tissueId === tissueId);
            return productCode?.productCode;
        },
        [productCodes]
    );

    const isLoading = isCaseFetching || isTissueFetching || isProductCodesFetching;

    return useMemo(
        () => ({
            case: _case,
            caseId: _case?.id,
            referral: _case?.referral,
            donor: _case?.donor,
            determination: _case?.determination,
            productCodes,
            getTissueProductCode,
            isLoading,
        }),
        [_case, getTissueProductCode, productCodes, isLoading]
    );
}
